import { FC } from 'react';

import { useFormik } from 'formik';
import { values } from 'remeda';
import useSWR from 'swr';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import {
  BONUS_RECEIVED_MAX,
  emptyLottery,
  EXTRA_OFFER_MAX,
  PARTNER_SPOTLIGHT_IMAGE_MAX_SIZE,
  PartnerSpotlightForm,
  validationSchema,
} from './form';
import { PartnerSpotlighFormLotterySection } from './partnerSpotlighFormLotterySection/PartnerSpotlighFormLotterySection';
import { PartnerSpotlightPreview } from './partnerSpotlightPreview/PartnerSpotlightPreview';
import { graphemeLength } from './utils';

import css from './PartnerSpotlightFormDialog.module.css';

import { casinoService } from 'v2/api/casinoService';
import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { PartnerSpotlightStatusesEnum } from 'v2/constants';

interface PartnerSpotlightFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: PartnerSpotlightForm;
  isSubmitting: boolean;
  onSubmit: (values: PartnerSpotlightForm, resetForm: VoidFunction) => void;
}

export const PartnerSpotlightFormDialog: FC<PartnerSpotlightFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const { data: casinos } = useSWR('getCasinos', casinoService.getCasinos);

  const formik = useFormik<PartnerSpotlightForm>({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    enableReinitialize: true,
  });

  const handleSelectImage = (field: string) => (value: string) => {
    formik.setFieldValue(field, value);
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  const isBackgroundOnly = formik.values.isBackgroundOnly;

  const handleBackgroundOnlyChange = (_: unknown, checked: boolean) => {
    formik.setFieldValue('isBackgroundOnly', checked);
    if (checked) {
      formik.setFieldValue('bonusReceivedText', '');
      formik.setFieldValue('extraOfferText', '');
      formik.setFieldValue('promoCode', '');
      formik.setFieldValue('lotteryEnabled', false);
      formik.setFieldValue('lottery', emptyLottery);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <div className={css.partnerSpotlightFormDialogLayout}>
          <form id="partnerSpotlightForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
            <TextField
              select
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Status"
              error={Boolean(formik.errors.status)}
              helperText={formik.errors.status}
              required
            >
              {values(PartnerSpotlightStatusesEnum).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>

            <FileSelector
              value={formik.values.backgroundImageId}
              label="Background Image"
              onSelect={handleSelectImage('backgroundImageId')}
              error={formik.errors.backgroundImageId}
              maxSize={PARTNER_SPOTLIGHT_IMAGE_MAX_SIZE}
              required
            />

            <FormControlLabel
              control={
                <Checkbox name="hasNewBadge" checked={formik.values.hasNewBadge} onChange={formik.handleChange} />
              }
              label="New"
            />

            <TextField
              name="bonusReceivedText"
              value={formik.values.bonusReceivedText}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Bonus received"
              error={Boolean(formik.errors.bonusReceivedText)}
              helperText={
                formik.errors.bonusReceivedText ??
                `${graphemeLength(formik.values.bonusReceivedText)}/${BONUS_RECEIVED_MAX}`
              }
              disabled={isBackgroundOnly}
            />

            <TextField
              name="extraOfferText"
              value={formik.values.extraOfferText}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Extra offer"
              error={Boolean(formik.errors.extraOfferText)}
              helperText={
                formik.errors.extraOfferText ?? `${graphemeLength(formik.values.extraOfferText)}/${EXTRA_OFFER_MAX}`
              }
              disabled={isBackgroundOnly}
            />

            <TextField
              name="promoCode"
              value={formik.values.promoCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Promo code"
              error={Boolean(formik.errors.promoCode)}
              helperText={formik.errors.promoCode}
              disabled={isBackgroundOnly}
            />

            <PartnerSpotlighFormLotterySection formik={formik} disabled={isBackgroundOnly} />

            <FormControlLabel
              control={
                <Checkbox
                  name="isBackgroundOnly"
                  checked={formik.values.isBackgroundOnly}
                  onChange={handleBackgroundOnlyChange}
                />
              }
              label="Background Only"
            />

            <TextField
              select
              name="partnerId"
              value={formik.values.partnerId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Partner (Casino)"
              error={Boolean(formik.errors.partnerId)}
              helperText={formik.errors.partnerId}
              required
            >
              <MenuItem value="">
                <em>Select Casino</em>
              </MenuItem>
              {casinos?.map((casino) => (
                <MenuItem key={casino.id} value={casino.id}>
                  {casino.name}
                </MenuItem>
              ))}
            </TextField>
          </form>

          <aside className={css.partnerSpotlightFormDialogPreview}>
            <PartnerSpotlightPreview values={formik.values} />
          </aside>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="partnerSpotlightForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
